import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Input,
} from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../../common/Loader";
import NoDataMessage from "../../../common/NoDataMessage";
import MenuBar from "../../../components/MenuBar";
import { Date as DateDisplay } from "../../../components/Date";
import {
  ContentFlagReason,
  ContentFlagRow,
  ContentFlagStatus,
  ContentQuestionRow,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { ConfirmModal } from "../ConfirmModal";
import { QuestionEditorModal } from "../QuestionEditorModal";
import { categoryLabel, useCategories } from "../useCategories";
import { ContentTable } from "../ContentTable";
import { UploadFlagsModal } from "./UploadFlagsModal";

const REASONS: ContentFlagReason[] = [
  "wrong_answer",
  "inappropriate",
  "too_hard",
  "other",
];

const resolutionColor = (action: string) => {
  if (action === "removed") return "danger";
  if (action === "dismissed") return "secondary";
  if (action === "restored") return "success";
  return "info";
};

export const ContentFlaggedPage: React.FC = () => {
  const { categories } = useCategories();
  const [status, setStatus] = useState<ContentFlagStatus>("open");
  const [flags, setFlags] = useState<ContentFlagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [questionsById, setQuestionsById] = useState<
    Record<string, ContentQuestionRow>
  >({});
  const [fixing, setFixing] = useState<{
    flag: ContentFlagRow;
    question: ContentQuestionRow;
  } | null>(null);
  const [reasonFilter, setReasonFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  /**
   * These two were guarded by a native `confirm()`, which looked nothing like
   * the styled dialogs guarding every other irreversible action in the module —
   * deleting a draft, clearing a lint list, publishing a set. It read as a
   * browser error rather than a decision, so it is a modal like the rest.
   */
  const [pendingResolve, setPendingResolve] = useState<{
    flag: ContentFlagRow;
    action: "removed" | "dismissed";
  } | null>(null);

  const visibleFlags = reasonFilter
    ? flags.filter((flag) => flag.reason === reasonFilter)
    : flags;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await Content.listFlags({ status });
      setFlags(items ?? []);

      // A flag stores only the question's title at capture time, so category
      // and difficulty come from the questions themselves. Failing this lookup
      // must not blank the queue — the flags are the point of the screen.
      const ids = (items ?? [])
        .map((flag) => flag.questionId)
        .filter((id): id is string => !!id);
      if (ids.length > 0) {
        try {
          const questions = await Content.questionsByIds(ids);
          setQuestionsById(
            questions.reduce<Record<string, ContentQuestionRow>>(
              (acc, question) => ({ ...acc, [question.id]: question }),
              {},
            ),
          );
        } catch (err) {
          // Blanking two columns silently is the hard-to-diagnose outcome the
          // note above warns about — keep the queue, but say why it is thinner.
          setQuestionsById({});
          toast.warn(
            `Category and difficulty unavailable: ${(err as Error).message}`,
          );
        }
      } else {
        setQuestionsById({});
      }
    } catch (err) {
      toast.error(`Failed to load flags: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (
    flag: ContentFlagRow,
    action: "removed" | "dismissed" | "restored" | "edited",
  ) => {
    setBusyId(flag.id);
    try {
      await Content.resolveFlag({ flagId: flag.id, action });
      toast.success(`Flag resolved as ${action}`);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const takeDown = (flag: ContentFlagRow) =>
    setPendingResolve({ flag, action: "removed" });

  const dismiss = (flag: ContentFlagRow) =>
    setPendingResolve({ flag, action: "dismissed" });

  /** Fix = edit the question, then mark the flag resolved as `edited`. */
  const startFix = async (flag: ContentFlagRow) => {
    if (!flag.questionId) {
      toast.error(
        "This flag has no linked question — it was backfilled without one",
      );
      return;
    }
    try {
      const [match] = await Content.questionsByIds([flag.questionId]);
      if (!match) {
        toast.error("Could not load the flagged question");
        return;
      }
      setFixing({ flag, question: match });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div>
      <MenuBar />
      <div className="container-fluid mt-3 pb-5">
        <Card className="mb-3">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h2 className="mb-1">Flagged content</h2>
                <div className="text-muted">
                  Removed questions awaiting a decision. Fix or dismiss puts one
                  back into the bank; take down keeps it out.
                </div>
              </div>
              <div>
                <Button
                  color="primary"
                  className="mr-2"
                  onClick={() => setUploading(true)}
                >
                  <i className="fas fa-upload mr-1" />
                  Upload flags
                </Button>
                <Button size="sm" color="secondary" outline onClick={load}>
                  Refresh
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <ButtonGroup size="sm" className="mr-3">
                <Button
                  color={status === "open" ? "primary" : "secondary"}
                  outline={status !== "open"}
                  onClick={() => setStatus("open")}
                >
                  Review queue
                </Button>
                <Button
                  color={status === "resolved" ? "primary" : "secondary"}
                  outline={status !== "resolved"}
                  onClick={() => setStatus("resolved")}
                >
                  Resolved
                </Button>
              </ButtonGroup>
              <Input
                type="select"
                className="custom-select"
                bsSize="sm"
                style={{ maxWidth: "12rem" }}
                value={reasonFilter}
                onChange={(e) => setReasonFilter(e.target.value)}
              >
                <option value="">All reasons</option>
                {REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason.replace("_", " ")}
                  </option>
                ))}
              </Input>
            </div>
          </CardHeader>
        </Card>

        {loading ? (
          <Loader light />
        ) : flags.length === 0 ? (
          <NoDataMessage light componentName="flag" />
        ) : (
          <ContentTable className="table table-sm table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ width: "14rem" }}>Category</th>
                <th style={{ width: "7rem" }}>Difficulty</th>
                <th style={{ width: "10rem" }}>Reason</th>
                <th>Comment</th>
                <th style={{ width: "10rem" }}>Customer</th>
                <th style={{ width: "14rem" }}>Flagged</th>
                {status === "resolved" ? (
                  <th style={{ width: "14rem" }}>Resolution</th>
                ) : (
                  <th style={{ width: "16rem" }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleFlags.map((flag) => (
                <tr key={flag.id}>
                  <td>{flag.questionTitle ?? flag.questionId ?? "—"}</td>
                  <td>
                    {flag.questionId && questionsById[flag.questionId] ? (
                      categoryLabel(
                        categories,
                        questionsById[flag.questionId].categoryId,
                      )
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {flag.questionId && questionsById[flag.questionId] ? (
                      questionsById[flag.questionId].difficulty
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <Badge color="warning">
                      {flag.reason.replace("_", " ")}
                    </Badge>
                  </td>
                  <td>{flag.comment ?? "—"}</td>
                  <td>{flag.customer ?? "—"}</td>
                  <td>
                    {flag.flaggedBy}
                    <br />
                    <small className="text-muted">
                      <DateDisplay date={new global.Date(flag.flaggedAt)} />
                    </small>
                  </td>
                  {status === "resolved" ? (
                    <td>
                      {flag.resolution ? (
                        <>
                          <Badge
                            color={resolutionColor(flag.resolution.action)}
                          >
                            {flag.resolution.action}
                          </Badge>
                          <br />
                          <small className="text-muted">
                            {flag.resolution.by} ·{" "}
                            <DateDisplay
                              date={new global.Date(flag.resolution.at)}
                            />
                          </small>
                          {flag.resolution.action === "removed" && (
                            <Button
                              size="sm"
                              color="success"
                              outline
                              className="mt-1 d-block"
                              disabled={busyId === flag.id}
                              onClick={() => resolve(flag, "restored")}
                            >
                              Restore
                            </Button>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  ) : (
                    <td>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        disabled={busyId === flag.id}
                        onClick={() => takeDown(flag)}
                      >
                        Take down
                      </Button>
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        className="ml-1"
                        disabled={busyId === flag.id}
                        onClick={() => startFix(flag)}
                      >
                        Fix
                      </Button>
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        className="ml-1"
                        disabled={busyId === flag.id}
                        title="Keeps the question and stops it being used as a generation negative example"
                        onClick={() => dismiss(flag)}
                      >
                        Dismiss
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </ContentTable>
        )}
      </div>

      <UploadFlagsModal
        isOpen={uploading}
        onClose={() => setUploading(false)}
        onImported={load}
      />

      {pendingResolve && (
        <ConfirmModal
          isOpen
          busy={busyId === pendingResolve.flag.id}
          title={
            pendingResolve.action === "removed"
              ? "Keep this question out of the bank?"
              : "Put this question back?"
          }
          confirmLabel={
            pendingResolve.action === "removed" ? "Take down" : "Dismiss report"
          }
          confirmColor={
            pendingResolve.action === "removed" ? "danger" : "primary"
          }
          onCancel={() => setPendingResolve(null)}
          onConfirm={() => {
            const { flag, action } = pendingResolve;
            setPendingResolve(null);
            void resolve(flag, action);
          }}
        >
          <p className="mb-2">
            <strong>
              {pendingResolve.flag.questionTitle ?? "This question"}
            </strong>
          </p>
          {pendingResolve.action === "removed" ? (
            <p className="mb-0 text-muted">
              It stays out of the bank and the report closes. You can still
              restore it from Resolved.
            </p>
          ) : (
            <p className="mb-0 text-muted">
              Use this when the report does not hold up — the question returns
              to the bank and players will see it again. Take down instead if
              the report was right. Once dismissed, this report offers no
              further action.
            </p>
          )}
        </ConfirmModal>
      )}

      {fixing && (
        <QuestionEditorModal
          question={fixing.question}
          categories={categories}
          isOpen
          onClose={() => setFixing(null)}
          onSaved={async () => {
            const { flag } = fixing;
            setFixing(null);
            // Two calls, sequenced client-side. If the resolve fails after the
            // edit saved, the flag stays open — safe, and the toast says so.
            try {
              await Content.resolveFlag({
                flagId: flag.id,
                action: "edited",
              });
              toast.success(
                "Question updated, flag resolved, and it is serving again",
              );
            } catch (err) {
              toast.error(
                `Edit saved, but the flag is still open: ${
                  (err as Error).message
                }`,
              );
            }
            load();
          }}
        />
      )}
    </div>
  );
};
